import {
  ActionList,
  ActionListItem,
  Alert,
  Box,
  Button,
  Divider,
  Dropdown,
  DropdownOverlay,
  ModalBody,
  ModalFooter,
  ModalHeader,
  SelectInput,
  StarIcon,
  TextArea,
  TextInput,
} from '@razorpay/blade/components';
import api from 'api';
import { GetSalaryStructureGraphQLRequest } from 'api/gql-utils/salaryStructures/types';
import gradientGray from 'assets/mss/gradient-gray.svg';
import { SalaryStructureComponent } from 'components/SalaryStructure/types';
import { FieldGroup } from 'components/WizardViews/components';
import { useWizardInfo } from 'components/WizardViews/providers/WizardInfoProvider';
import { useFormWithProvider } from 'forms';
import React from 'react';
import { useQuery } from 'react-query';
import { AppError } from 'utils/AppError';
import { salaryStructureQueries } from '../constants';
import PreviewSelectedStructure from './components/PreviewSelectedStructure';
import { useListSalaryStructures } from '../hooks';
import { SalaryStructureFormData } from './types';
import { basicDetailsStepValidationSchema } from './utils';

const BasicDetailsStep = () => {
  const { updateStepStatus, setCurrentStep, closeWizard } = useWizardInfo();
  const { register, validate, formData, setFieldValue, setFormData } =
    useFormWithProvider<SalaryStructureFormData>();

  const isCreationTypeFromTemplate = formData.creationType === 'from-existing';

  useQuery({
    queryKey: [
      salaryStructureQueries.getSalaryStructure,
      isCreationTypeFromTemplate,
      formData.templateId,
    ],
    queryFn: () => {
      let input: GetSalaryStructureGraphQLRequest;
      if (isCreationTypeFromTemplate && formData.templateId) {
        input = {
          input: {
            id: formData.templateId,
          },
        };
      } else {
        input = {
          input: {
            type: 'SALARY_STRUCTURE_TYPE_DEFAULT',
          },
        };
      }
      return api.salaryStructures.queries.getSalaryStructure(input);
    },
    onError: (error: AppError) => {},
    onSuccess(data) {
      const salaryStructureComponents =
        data.payrollGetSalaryStructure.data.salaryStructureComponents;
      const components = salaryStructureComponents.reduce((acc, component) => {
        acc[component.salaryComponent.id] = component;
        return acc;
      }, {} as Record<string, SalaryStructureComponent>);
      setFormData({
        ...formData,
        templateId: data.payrollGetSalaryStructure.data.id,
        components,
        componentIds: salaryStructureComponents.map((component) => component.id),
      });
    },
  });

  const { data, isFetching, error, refetch } = useListSalaryStructures({
    enabled: isCreationTypeFromTemplate,
  });

  const templates = data ?? [];

  const handleNextClick = () => {
    if (validate(basicDetailsStepValidationSchema)) {
      updateStepStatus(0, 'success');
      setCurrentStep(1);
    }
  };

  const handleBackClick = () => {
    closeWizard();
    // Close the modal and refresh the list view data
  };

  const handleTemplateChange = (template: string) => {
    setFieldValue('templateId', template);
  };

  if (error) {
    return (
      <>
        <ModalHeader leading={<StarIcon />} title="Create structure" />
        <ModalBody height="100%">
          <Alert
            color="negative"
            title="Error"
            description={error.message}
            actions={{
              primary: { text: 'Retry', onClick: refetch },
            }}
          />
        </ModalBody>
      </>
    );
  }

  return (
    <>
      <ModalHeader leading={<StarIcon />} title="Create structure" />
      <ModalBody height="100%">
        <Box
          display="flex"
          gap="spacing.11"
          height="100%"
          backgroundImage={`url("${gradientGray}")`}
          backgroundRepeat="no-repeat"
          backgroundSize="contain"
          backgroundPosition="right bottom">
          <Box flex="1">
            <FieldGroup title="General details">
              <TextInput
                necessityIndicator="required"
                label="Structure name"
                placeholder="Structure name"
                {...register('name')}
              />
              <TextArea
                necessityIndicator="required"
                label="Description"
                placeholder="Description"
                {...register('description')}
              />
            </FieldGroup>

            {isCreationTypeFromTemplate && (
              <>
                <Divider marginY="spacing.8" />

                <FieldGroup title="Select template">
                  <Dropdown>
                    <SelectInput
                      label="Existing structure"
                      placeholder="Select existing structure"
                      helpText="Selected template will be used for creating the new structure"
                      {...register('templateId', 'select')}
                      onChange={({ values }) => handleTemplateChange(values[0] as string)}
                    />
                    <DropdownOverlay>
                      <ActionList>
                        {templates.map((item) => (
                          <ActionListItem
                            key={item.id}
                            title={item.name}
                            value={item.id}
                            description={item.description}
                          />
                        ))}
                      </ActionList>
                    </DropdownOverlay>
                  </Dropdown>
                </FieldGroup>
              </>
            )}
          </Box>
          <Box width="calc(max(30%, 400px))" height="100%">
            <PreviewSelectedStructure isPreviewHidden={!isCreationTypeFromTemplate} />
          </Box>
        </Box>
      </ModalBody>
      <ModalFooter>
        <Box display="flex" justifyContent="space-between">
          <Button variant="tertiary" onClick={handleBackClick} isDisabled={isFetching}>
            Back: All templates
          </Button>
          <Button onClick={handleNextClick} isDisabled={isFetching}>
            Next
          </Button>
        </Box>
      </ModalFooter>
    </>
  );
};

export default BasicDetailsStep;
